/**
 * Hook for preventing memory leaks by managing component cleanup
 */

import { useEffect, useRef, useCallback } from "react"

/**
 * Hook that tracks if a component is still mounted to prevent memory leaks
 * @returns object with isMounted ref and safeSetState function
 */
export const useIsMounted = () => {
  const isMountedRef = useRef<boolean>(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return isMountedRef
}

/**
 * Hook that provides a safe setState function that only executes if component is mounted
 * @param setState - The setState function to wrap
 * @returns Safe setState function
 */
export const useSafeSetState = <T>(setState: (value: T | ((prev: T) => T)) => void) => {
  const isMountedRef = useIsMounted()

  return useCallback(
    (value: T | ((prev: T) => T)) => {
      if (isMountedRef.current) {
        setState(value)
      }
    },
    [setState, isMountedRef],
  )
}

/**
 * Hook for safely executing async operations with cleanup
 * @returns object with executeAsync function and cleanup
 */
export const useAsyncOperation = () => {
  const isMountedRef = useIsMounted()
  const activeOperations = useRef<Set<Promise<unknown>>>(new Set())

  // Helper to check mounted state and call callback safely
  const safeCallback = <T>(isMounted: boolean, callback: ((arg: T) => void) | undefined, arg: T) => {
    if (isMounted && callback) {
      callback(arg)
    }
  }

  const executeAsync = useCallback(
    async <T>(
      asyncOperation: () => Promise<T>,
      onSuccess?: (result: T) => void,
      onError?: (error: unknown) => void,
    ): Promise<void> => {
      if (!isMountedRef.current) {
        return
      }

      try {
        const operation = asyncOperation()
        activeOperations.current.add(operation)

        const result = await operation

        // Check if component is still mounted before calling success callback
        safeCallback(isMountedRef.current, onSuccess, result)
      } catch (error) {
        // Check if component is still mounted before calling error callback
        safeCallback(isMountedRef.current, onError, error)
      }
    },
    [isMountedRef],
  )

  // Очистка активных операций при размонтировании
  useEffect(() => {
    // Read out here, not in the cleanup: by unmount the ref may point at a different set, and the
    // rule says so. This is the set the effect was set up with, which is the one to clear.
    const operations = activeOperations.current
    return () => {
      operations.clear()
    }
  }, [])

  return { executeAsync, isMounted: isMountedRef }
}

/**
 * Hook for managing timers with automatic cleanup
 * @returns object with timer management functions
 */
export const useTimer = () => {
  const timers = useRef<Set<NodeJS.Timeout>>(new Set())
  const isMountedRef = useIsMounted()

  const setTimeout = useCallback(
    (callback: () => void, delay: number): NodeJS.Timeout | null => {
      if (!isMountedRef.current) {
        return null
      }

      const timer = globalThis.setTimeout(() => {
        timers.current.delete(timer)
        if (isMountedRef.current) {
          callback()
        }
      }, delay)

      timers.current.add(timer)
      return timer
    },
    [isMountedRef],
  )

  const setInterval = useCallback(
    (callback: () => void, delay: number): NodeJS.Timeout | null => {
      if (!isMountedRef.current) {
        return null
      }

      const timer = globalThis.setInterval(() => {
        if (isMountedRef.current) {
          callback()
        } else {
          globalThis.clearInterval(timer)
          timers.current.delete(timer)
        }
      }, delay)

      timers.current.add(timer)
      return timer
    },
    [isMountedRef],
  )

  const clearTimer = useCallback((timer: NodeJS.Timeout) => {
    globalThis.clearTimeout(timer)
    globalThis.clearInterval(timer)
    timers.current.delete(timer)
  }, [])

  // Очистка всех таймеров при размонтировании
  useEffect(() => {
    const currentTimers = timers.current
    return () => {
      currentTimers.forEach((timer) => {
        globalThis.clearTimeout(timer)
        globalThis.clearInterval(timer)
      })
      currentTimers.clear()
    }
  }, [])

  return { setTimeout, setInterval, clearTimer }
}
